import _ from 'lodash';
import { GeoPoint } from '../config/firebase';
import { GOOGLE_MAPS_API_KEY } from '../config/googleMaps';

import Address from '../models/interfaces/address';
import Region from '../models/interfaces/region';

const regionsJson = require('../assets/data/regions.json');

const invalidAddressError = () => new Error('Endereço inválido, verifique o CEP');

type LatLngPoint = { lat: number; lng: number };

export type GoogleAddressResult = {
  geometry: {
    location: LatLngPoint;
    bounds?: { northeast: LatLngPoint; southwest: LatLngPoint };
    viewport?: { northeast: LatLngPoint; southwest: LatLngPoint };
  };
  addressComponents: { longName: string; shortName: string; types: string[] }[];
  placeId: string;
};

let mapsScript: Promise<void> | null = null;

const loadMapsScript = () => {
  if (typeof google !== 'undefined' && (google.maps?.Geocoder || google.maps?.importLibrary)) {
    return Promise.resolve();
  }
  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(invalidAddressError());
  }
  if (!mapsScript) {
    mapsScript = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&v=weekly`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        mapsScript = null;
        reject(invalidAddressError());
      };
      document.head.appendChild(script);
    });
  }
  return mapsScript;
};

const createGeocoder = async () => {
  await loadMapsScript();
  if (google.maps.Geocoder) {
    return new google.maps.Geocoder();
  }
  const library = await google.maps.importLibrary('geocoding') as google.maps.GeocodingLibrary;
  return new library.Geocoder();
};

const readLatLng = (point: google.maps.LatLng | google.maps.LatLngLiteral): LatLngPoint => {
  if (typeof (point as google.maps.LatLng).lat === 'function') {
    return {
      lat: (point as google.maps.LatLng).lat(),
      lng: (point as google.maps.LatLng).lng()
    };
  }
  const literal = point as google.maps.LatLngLiteral;
  return { lat: literal.lat, lng: literal.lng };
};

const readBounds = (bounds?: google.maps.LatLngBounds) => {
  if (!bounds) return undefined;
  return {
    northeast: readLatLng(bounds.getNorthEast()),
    southwest: readLatLng(bounds.getSouthWest())
  };
};

const formatAddress = (address: Address) => [
  address.number,
  address.street,
  address.neighborhood,
  address.city,
  address.state,
  address.zipCode,
  'Brasil'
].filter((part) => part).join(' ');

const geocodeAddress = async (rowAddress: string): Promise<GoogleAddressResult | null> => {
  try {
    const geocoder = await createGeocoder();
    console.log('rowAddress', rowAddress);
    const { results } = await geocoder.geocode({ address: rowAddress, region: 'br' });
    const result = results?.[0];
    if (!result?.geometry?.location) return null;

    return {
      geometry: {
        location: readLatLng(result.geometry.location),
        bounds: readBounds(result.geometry.bounds),
        viewport: readBounds(result.geometry.viewport)
      },
      addressComponents: (result.address_components ?? []).map((component) => ({
        longName: component.long_name,
        shortName: component.short_name,
        types: [...component.types]
      })),
      placeId: result.place_id ?? ''
    };
  } catch {
    return null;
  }
};

export const viacep = (zipCode: string) => fetch(`https://viacep.com.br/ws/${zipCode}/json`)
  .then((response) => response.json())
  .then((data) => data);

export const getGeocoding = async (address: Address): Promise<GeoPoint | null> => {
  console.log('address', address);
  console.log('formatAddress', formatAddress(address));
  const found = await geocodeAddress(formatAddress(address));
  console.log('found geocoding', found);
  if (!found) return null;
  const { lat, lng } = found.geometry.location;
  return new GeoPoint(lat, lng);
};

export const getPlaceGeocodingPlus = async (neighborhood: string, city: string, state: string): Promise<any | null> => {
  const found = await geocodeAddress(`${neighborhood} ${city} ${state}`);
  if (!found) return null;
  const { lat, lng } = found.geometry.location;
  const corners = found.geometry.bounds || found.geometry.viewport;
  return {
    coords: new GeoPoint(lat, lng),
    placeId: found.placeId,
    bounds: corners
      ? { northeast: corners.northeast, southwest: corners.southwest }
      : null
  };
};

export const getPlaceGeocoding = async (neighborhood: string, city: string, state: string): Promise<GeoPoint | null> => {
  const found = await geocodeAddress(`${neighborhood} ${city} ${state}`);
  if (!found) return null;
  const { lat, lng } = found.geometry.location;
  return new GeoPoint(lat, lng);
};

export const getGeoPoint = (lat: number, lng: number): GeoPoint => new GeoPoint(lat, lng);

export const getAddressByGoogle = (address: Address) => geocodeAddress(formatAddress(address));

export const setAddressByCEP = (ev: any, setFieldValue: any): Promise<any> => {
  const { value } = ev.target;
  const cep = value?.replace(/[^0-9]/g, '');
  if (cep?.length !== 8) return Promise.resolve();

  return viacep(cep).then((data) => {
    if (data.erro) {
      throw new Error('CEP inválido');
    }

    setFieldValue('address.zipCode', cep);
    setFieldValue('address.street', data.logradouro);
    setFieldValue('address.neighborhood', data.bairro);
    setFieldValue('address.city', data.localidade);
    setFieldValue('address.state', data.uf);
    setFieldValue('address.number', '');
    setFieldValue('address.complement', '');
    setFieldValue('address.ibge', data.ibge);
  });
};

export const getRegion = (city: string): Region | null => _.find(regionsJson, (x) => x.MUNICIPIO === city) ?? null;
